import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const OPTIONS = ['unlisted', 'private'];

export default function PlanVisibilityModal({ isOpen, onClose, value, canEdit, saving, onSave }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(value || 'unlisted');

  useEffect(() => {
    if (isOpen) setSelected(value || 'unlisted');
  }, [isOpen, value]);

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Jolkia Plan',
          url: url
        });
      } catch (e) {
        // ignore abort
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        alert(t('plan.visibility.copied'));
      } catch (e) {
        console.error('Failed to copy', e);
      }
    }
  };

  if (!isOpen) return null;

  const isPublic = selected === 'unlisted' || selected === 'open';

  return (
    <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-100">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-white/95 backdrop-blur-md">
          <div className="text-xl font-black text-gray-900 tracking-tight">{t('plan.visibility.title')}</div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700 font-bold p-2 rounded-full hover:bg-gray-100 transition-colors">
            ✕
          </button>
        </div>

        <div className="p-6 space-y-3">
          {OPTIONS.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => setSelected(opt)}
              disabled={!canEdit}
              className={[
                'w-full text-left border-2 rounded-2xl p-5 transition-all duration-200 group',
                !canEdit ? 'opacity-60 cursor-not-allowed' : 'hover:border-indigo-300 hover:bg-indigo-50/30 cursor-pointer',
                selected === opt ? 'border-indigo-500 bg-indigo-50 shadow-inner' : 'border-gray-100'
              ].join(' ')}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className={`text-base font-black ${selected === opt ? 'text-indigo-900' : 'text-gray-900'}`}>{t(`plan.visibility.${opt}.title`)}</div>
                  <div className="mt-1 text-sm text-gray-500 font-medium">{t(`plan.visibility.${opt}.subtitle`)}</div>
                </div>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selected === opt ? 'border-indigo-600 bg-indigo-600' : 'border-gray-300 group-hover:border-gray-400'}`}>
                  {selected === opt && <div className="w-2 h-2 rounded-full bg-white" />}
                </div>
              </div>
            </button>
          ))}
        </div>

        {isPublic && (
          <div className="px-6 pb-2">
            <button
              type="button"
              onClick={handleShare}
              className="w-full py-3 px-4 bg-blue-50 text-blue-700 font-bold rounded-2xl border border-blue-100 hover:bg-blue-100 hover:border-blue-200 transition-all flex items-center justify-center gap-2 shadow-sm"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
              </svg>
              {t('plan.visibility.share')}
            </button>
          </div>
        )}

        <div className="p-6 border-t border-gray-100 bg-gray-50/50 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-full font-bold text-gray-600 hover:bg-gray-200 transition-colors text-sm uppercase tracking-wide"
          >
            {t('workout.cancel')}
          </button>
          <button
            type="button"
            disabled={!canEdit || saving}
            onClick={() => onSave?.(selected)}
            className={[
              'px-6 py-2.5 rounded-full font-bold text-white shadow-lg transition-all duration-200 text-sm uppercase tracking-wide',
              !canEdit || saving 
                ? 'bg-indigo-300 cursor-not-allowed shadow-none' 
                : 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-500/30 hover:shadow-indigo-500/40 hover:-translate-y-0.5'
            ].join(' ')}
          >
            {saving ? t('plan.saving') : t('plan.visibility.save')}
          </button>
        </div>
      </div>
    </div>
  );
}


